/**
 * AgentChatButton — Floating chat bubble for marketing workflow assistant.
 *
 * Behavior:
 *   - Bottom-right floating button (FAB).
 *   - Click → opens a small chat panel.
 *   - Each user message is converted into a prompt on the frontend.
 *   - Frontend creates a text task through the ai-controller proxy (/api/ai/v1).
 *   - Returned response is parsed as plan JSON and executed locally on canvas.
 *   - If the reply contains [data-tour="..."], driver.js highlights it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { getAgentContext } from '../../services/agent/agentContext';
import { highlightSelector, extractTourSelector } from '../../services/agent/driverBridge';
import { parsePlan, executePlan } from '../../services/agent/toolExecutor';
import { callAgent, type LLMMessage } from '../../services/agent/llmProvider';

/* Inject keyframes once into the document head */
const STYLE_ID = 'tada-agent-glow-style';
if (!document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes tadaAgentGlow {
      0%   { box-shadow: 0 0 0 0 rgba(0,104,255,0.7), 0 4px 14px rgba(0,0,0,0.2); }
      60%  { box-shadow: 0 0 0 14px rgba(0,104,255,0), 0 4px 14px rgba(0,0,0,0.2); }
      100% { box-shadow: 0 0 0 0 rgba(0,104,255,0), 0 4px 14px rgba(0,0,0,0.2); }
    }
  `;
  document.head.appendChild(s);
}

interface ChatMessage {
  role: 'user' | 'agent';
  text: string;
  toolActions?: string[]; // brief labels of tools executed during this turn
}

const MODEL_LABEL = 'AI Controller (text2text)';

/** Conversation history for multi-turn context (system prompt excluded). */
// LLMMessage is imported from llmProvider

const MAX_TOOL_ITERATIONS = 6; // prevent infinite loops

/** Delay between consecutive add_node calls so Date.now() IDs are always unique. */
const ADD_NODE_DELAY_MS = 1200;

// ---------------------------------------------------------------------------
// Rate limiting — 3 messages per hour, stored in localStorage
// ---------------------------------------------------------------------------
const RATE_LIMIT_KEY = 'tada_agent_rl';
const RATE_LIMIT_MAX = 10;
const RATE_LIMIT_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

interface RateLimitStore {
  timestamps: number[];
}

function getRateLimitStore(): RateLimitStore {
  try {
    const raw = localStorage.getItem(RATE_LIMIT_KEY);
    if (raw) return JSON.parse(raw) as RateLimitStore;
  } catch { /* ignore */ }
  return { timestamps: [] };
}

function saveRateLimitStore(store: RateLimitStore): void {
  try {
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(store));
  } catch { /* ignore */ }
}

/** Returns remaining messages allowed this hour. */
function getRemainingQuota(): number {
  const now = Date.now();
  const store = getRateLimitStore();
  const recent = store.timestamps.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
  return Math.max(0, RATE_LIMIT_MAX - recent.length);
}

/** Records one message usage. Returns false if quota exhausted. */
function consumeQuota(): boolean {
  const now = Date.now();
  const store = getRateLimitStore();
  const recent = store.timestamps.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
  if (recent.length >= RATE_LIMIT_MAX) return false;
  recent.push(now);
  saveRateLimitStore({ timestamps: recent });
  return true;
}

/** ms until the oldest slot expires (i.e. until quota refreshes). */
function msUntilReset(): number {
  const now = Date.now();
  const store = getRateLimitStore();
  const recent = store.timestamps
    .filter((t) => now - t < RATE_LIMIT_WINDOW_MS)
    .sort((a, b) => a - b);
  if (recent.length < RATE_LIMIT_MAX) return 0;
  return RATE_LIMIT_WINDOW_MS - (now - recent[0]);
}

function formatCountdown(ms: number): string {
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m} phút ${s} giây` : `${s} giây`;
}

/**
 * Single-shot call to svc-business-marketing (backend will call wkr-ai-controller).
 */
async function callLLMSingleShot(
  ctx: ReturnType<typeof getAgentContext>,
  history: LLMMessage[],
  userText: string,
): Promise<string> {
  return callAgent(ctx, history, userText);
}

export const AgentChatButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('đang suy nghĩ...');
  const [agentError, setAgentError] = useState<string | null>(null);
  const [quota, setQuota] = useState(() => getRemainingQuota());
  const [countdownLabel, setCountdownLabel] = useState('');

  // Single always-running interval — syncs quota + countdown from localStorage every second.
  // This ensures the display resets automatically when slots expire, regardless of
  // when the component was mounted or how many messages have expired.
  useEffect(() => {
    const tick = () => {
      const q = getRemainingQuota();
      setQuota(q);
      setCountdownLabel(q === 0 ? formatCountdown(msUntilReset()) : '');
    };
    tick(); // run immediately on mount
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []); // empty deps — runs once, interval lives for component lifetime

  // Multi-turn conversation history (roles: user/assistant/tool only)
  const historyRef = useRef<LLMMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, busyLabel]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;

    // Rate limit check
    if (!consumeQuota()) {
      const ms = msUntilReset();
      setMessages((prev) => [
        ...prev,
        { role: 'user', text },
        {
          role: 'agent',
          text: `⏳ Bạn đã dùng hết ${RATE_LIMIT_MAX} tin nhắn trong giờ này.\n\nVui lòng thử lại sau **${formatCountdown(ms)}** hoặc liên hệ support để mở thêm giới hạn truy cập.`,
        },
      ]);
      setInput('');
      setQuota(0);
      return;
    }
    setQuota(getRemainingQuota());

    setMessages((prev) => [...prev, { role: 'user', text }]);
    setInput('');
    setBusy(true);
    setBusyLabel('đang suy nghĩ...');
    setAgentError(null);

    const toolActionsThisTurn: string[] = [];

    const TOOL_LABELS: Record<string, string> = {
      add_node: '➕ Đang thêm node...',
      add_edge: '🔗 Đang kết nối node...',
      delete_node: '🗑 Đang xóa node...',
    };

    try {
      const ctx = getAgentContext();

      // Single API call — backend builds prompt and returns JSON-plan text
      const rawText = await callLLMSingleShot(ctx, historyRef.current, text);

      const plan = parsePlan(rawText);

      let replyText: string;

      if (plan && plan.plan.length > 0) {
        // Execute the plan locally — no more API calls needed
        await executePlan(
          plan.plan,
          (toolName) => {
            setBusyLabel(TOOL_LABELS[toolName] ?? `⚙️ ${toolName}...`);
            toolActionsThisTurn.push(toolName);
          },
          ADD_NODE_DELAY_MS,
        );
        replyText = plan.message;
      } else if (plan) {
        // plan.plan is empty — pure Q&A response
        replyText = plan.message;
      } else {
        // LLM returned plain text (not JSON) — show as-is
        replyText = rawText;
      }

      // Store summary text in history (not raw JSON)
      historyRef.current = [
        ...historyRef.current,
        { role: 'user', content: text },
        { role: 'assistant', content: replyText },
      ];
      if (historyRef.current.length > 20) {
        historyRef.current = historyRef.current.slice(-20);
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'agent',
          text: replyText,
          toolActions: toolActionsThisTurn,
        },
      ]);

      // Auto-highlight any [data-tour="..."] the LLM mentions
      const sel = extractTourSelector(replyText);
      if (sel) {
        highlightSelector(sel, {
          title: '💡 Trợ lý gợi ý',
          description: 'Vị trí bạn cần xem',
        });
      }
    } catch (err: any) {
      const msg = err?.message || String(err);
      setAgentError(msg);
      setMessages((prev) => [...prev, { role: 'agent', text: `⚠️ ${msg}` }]);
    } finally {
      setBusy(false);
      setBusyLabel('đang suy nghĩ...');
    }
  }, [input, busy]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* FAB toggle */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Đóng trợ lý' : 'Mở trợ lý'}
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: '#0068FF',
          color: 'white',
          border: 'none',
          boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
          cursor: 'pointer',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: open ? 'none' : 'tadaAgentGlow 2s ease-out infinite',
        }}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          style={{
            position: 'fixed',
            right: 20,
            bottom: 84,
            width: 360,
            maxWidth: 'calc(100vw - 40px)',
            height: 480,
            maxHeight: 'calc(100vh - 120px)',
            background: 'white',
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9999,
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid #f3f4f6',
              fontWeight: 600,
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <MessageCircle size={16} color="#0068FF" />
            <span>Trợ lý Tadagram</span>
            <span style={{ marginLeft: 8, fontSize: 10, color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 999, padding: '2px 8px' }}>
              {MODEL_LABEL}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: quota > 0 ? '#9ca3af' : '#ef4444' }}>
              {quota}/{RATE_LIMIT_MAX} tin
            </span>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 12,
              background: '#fafafa',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {messages.length === 0 && (
              <div
                style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: '10px 12px',
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: '#111827',
                  marginBottom: 8,
                }}
              >
                Chào bạn! Nếu bạn muốn setup workflow, có thể nhờ mình nhé 😊
                <br />
                Ví dụ hãy thử nói:
                <div
                  style={{
                    marginTop: 8,
                    background: '#f0f7ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: 8,
                    padding: '7px 10px',
                    color: '#1d4ed8',
                    fontStyle: 'italic',
                    fontSize: 12,
                  }}
                >
                  "Tôi muốn workflow này có thêm chức năng đăng bài vào group..."
                </div>
                <div style={{ marginTop: 6, fontSize: 11, color: '#6b7280' }}>
                  Mình sẽ trực tiếp thêm node và kết nối vào canvas cho bạn.
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  marginBottom: 10,
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                  flexDirection: 'column',
                  alignItems: m.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                {/* Tool action badges — shown above agent reply */}
                {m.role === 'agent' && m.toolActions && m.toolActions.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
                    {m.toolActions.map((t, ti) => {
                      const BADGE: Record<string, string> = {
                        add_node: '➕ node đã thêm',
                        add_edge: '🔗 edge đã nối',
                        delete_node: '🗑 node đã xóa',
                      };
                      return (
                        <span
                          key={ti}
                          style={{
                            fontSize: 10,
                            padding: '2px 6px',
                            borderRadius: 10,
                            background: '#dcfce7',
                            color: '#166534',
                            border: '1px solid #bbf7d0',
                          }}
                        >
                          {BADGE[t] ?? t}
                        </span>
                      );
                    })}
                  </div>
                )}
                <div
                  style={{
                    maxWidth: '85%',
                    padding: '8px 12px',
                    borderRadius: 12,
                    background: m.role === 'user' ? '#0068FF' : 'white',
                    color: m.role === 'user' ? 'white' : '#111827',
                    border: m.role === 'user' ? 'none' : '1px solid #e5e7eb',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6b7280' }}>
                <Loader2 size={14} className="animate-spin" /> {busyLabel}
              </div>
            )}
          </div>

          {/* Input */}
          <div
            style={{
              borderTop: '1px solid #f3f4f6',
              padding: 10,
              display: 'flex',
              gap: 6,
              alignItems: 'flex-end',
            }}
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={quota === 0 ? `Thử lại sau ${countdownLabel}` : 'Hỏi trợ lý...'}
              disabled={quota === 0}
              rows={2}
              style={{
                flex: 1,
                resize: 'none',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '6px 8px',
                fontSize: 13,
                fontFamily: 'inherit',
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={busy || !input.trim() || quota === 0}
              style={{
                background: busy || !input.trim() || quota === 0 ? '#9ca3af' : '#0068FF',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '8px 10px',
                cursor: busy || !input.trim() || quota === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              aria-label="Gửi"
            >
              <Send size={16} />
            </button>
          </div>

          {agentError && !busy && (
            <div
              style={{
                padding: '6px 10px',
                background: '#fef2f2',
                color: '#b91c1c',
                fontSize: 11,
                borderTop: '1px solid #fecaca',
              }}
            >
              {agentError}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AgentChatButton;
